import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { X, Check, ChevronRight, Upload, Palette } from 'lucide-react';
import { weddingThemes, themeCategories, getThemeById } from '../../data/weddingThemes';

export const BookDesignSelector = ({ selectedTheme, onSelectTheme, formData, onChange }) => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [previewTheme, setPreviewTheme] = useState(null);
  const [showCustomize, setShowCustomize] = useState(false);

  const filtered = useMemo(() => {
    if (activeCategory === 'all') return weddingThemes;
    return weddingThemes.filter(t => t.category === activeCategory);
  }, [activeCategory]);

  const selected = getThemeById(selectedTheme);

  const handleSelect = (themeId) => {
    onSelectTheme(themeId);
    setPreviewTheme(null);
    setShowCustomize(true);
  };

  return (
    <div data-testid="book-design-selector" className="animate-fade-in-up space-y-6">
      <div className="text-center mb-2">
        <h2 className="text-xl font-serif font-bold text-stone-900 mb-1">Choose the visual style for your planner</h2>
        <p className="text-xs font-sans text-stone-500 max-w-lg mx-auto">
          The design you choose applies to the entire wedding planner — cover, pages, checklists, budget and more. You can always change it later.
        </p>
      </div>

      {/* Category tabs */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
        {themeCategories.map(cat => (
          <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
            className={`text-xs font-sans font-semibold px-3.5 py-2 rounded-full whitespace-nowrap transition-all ${
              activeCategory === cat.id ? 'bg-stone-800 text-white' : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
            }`}>{cat.label}</button>
        ))}
      </div>

      {/* Theme grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filtered.map(theme => {
          const isSelected = selectedTheme === theme.id;
          return (
            <div key={theme.id} data-testid={`theme-card-${theme.id}`}
              className={`group relative rounded-2xl overflow-hidden border-2 transition-all cursor-pointer hover:shadow-xl hover:-translate-y-1 ${
                isSelected ? 'border-primary ring-2 ring-primary/20 shadow-lg' : 'border-stone-200 hover:border-stone-400'
              }`}
              onClick={() => setPreviewTheme(theme)}>
              <div className="aspect-square overflow-hidden">
                <img src={theme.coverImage} alt={theme.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              </div>
              <div className="p-3 bg-white">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-sm font-sans font-semibold text-stone-800 truncate">{theme.name}</p>
                  {isSelected && <Check className="w-4 h-4 text-primary flex-shrink-0" />}
                </div>
                <div className="flex items-center gap-1">
                  {theme.colors.slice(0, 5).map((c, i) => (
                    <div key={i} className="w-4 h-4 rounded-full border border-stone-200/80" style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Preview modal (portaled) */}
      {previewTheme && ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }} onClick={() => setPreviewTheme(null)}>
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="relative">
              <img src={previewTheme.coverImage} alt={previewTheme.name} className="w-full aspect-[4/3] object-cover rounded-t-3xl" />
              <button onClick={() => setPreviewTheme(null)} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/60">
                <X className="w-4 h-4" />
              </button>
              {selectedTheme === previewTheme.id && (
                <div className="absolute top-4 left-4 bg-primary text-white text-[10px] font-sans font-bold uppercase px-3 py-1 rounded-full">Valgt</div>
              )}
            </div>
            <div className="p-6 space-y-5">
              <div>
                <h3 className="text-xl font-serif font-bold text-stone-900">{previewTheme.name}</h3>
                <p className="text-sm font-sans text-stone-500 mt-1 leading-relaxed">{previewTheme.description}</p>
              </div>

              <div>
                <p className="text-[10px] font-sans font-bold text-stone-400 uppercase tracking-widest mb-2">Fargepalett</p>
                <div className="flex items-center gap-2">
                  {previewTheme.colors.map((c, i) => (
                    <div key={i} className="flex flex-col items-center gap-1">
                      <div className="w-10 h-10 rounded-xl border border-stone-200" style={{ backgroundColor: c }} />
                      <span className="text-[9px] font-sans text-stone-400">{previewTheme.colorNames[i]}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-[10px] font-sans font-bold text-stone-400 uppercase tracking-widest mb-2">Typografi</p>
                <div className="flex gap-6">
                  <div>
                    <p className="text-lg text-stone-800" style={{ fontFamily: previewTheme.fonts.heading }}>Heading</p>
                    <p className="text-[10px] font-sans text-stone-400">{previewTheme.fonts.heading}</p>
                  </div>
                  <div>
                    <p className="text-sm text-stone-600" style={{ fontFamily: previewTheme.fonts.body }}>Body text og detaljer</p>
                    <p className="text-[10px] font-sans text-stone-400">{previewTheme.fonts.body}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Palette className="w-3.5 h-3.5 text-stone-400" />
                <p className="text-xs font-sans text-stone-500">{previewTheme.mood}</p>
              </div>

              {/* Sample page preview with theme colors */}
              <div className="rounded-2xl border overflow-hidden" style={{ borderColor: previewTheme.colors[1] + '40' }}>
                <div className="px-6 py-4" style={{ backgroundColor: previewTheme.colors[4] || previewTheme.colors[0] }}>
                  <p className="text-[10px] font-sans font-bold uppercase tracking-widest mb-2" style={{ color: previewTheme.colors[1], fontFamily: previewTheme.fonts.body }}>Eksempel – Checkliste</p>
                  <p className="text-lg font-bold mb-3" style={{ color: previewTheme.colors[3], fontFamily: previewTheme.fonts.heading }}>Things to do</p>
                  {['Book venue', 'Send invitations', 'Choose flowers', 'Book photographer'].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 py-1.5" style={{ borderBottom: `1px solid ${previewTheme.colors[1]}15` }}>
                      <div className="w-4 h-4 rounded border-2 flex items-center justify-center" style={{ borderColor: previewTheme.colors[1] }}>
                        {i < 2 && <Check className="w-2.5 h-2.5" style={{ color: previewTheme.colors[1] }} />}
                      </div>
                      <span className="text-xs" style={{ color: previewTheme.colors[3], fontFamily: previewTheme.fonts.body, textDecoration: i < 2 ? 'line-through' : 'none', opacity: i < 2 ? 0.5 : 1 }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button data-testid={`select-theme-${previewTheme.id}`} onClick={() => handleSelect(previewTheme.id)}
                className="w-full bg-primary text-white font-sans font-semibold py-3.5 rounded-full hover:bg-primary/90 transition-colors flex items-center justify-center gap-2">
                {selectedTheme === previewTheme.id ? 'Already selected' : 'Choose this theme'} <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Customization panel */}
      {selected && showCustomize && (
        <div data-testid="theme-customization" className="bg-white rounded-2xl border border-stone-100 p-6 space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={selected.coverImage} alt="" className="w-12 h-12 rounded-xl object-cover" />
              <div>
                <p className="text-sm font-sans font-semibold text-stone-800">{selected.name}</p>
                <p className="text-[10px] font-sans text-stone-400">Tilpass designet</p>
              </div>
            </div>
            <button onClick={() => { onSelectTheme(''); setShowCustomize(false); }} className="text-xs font-sans text-stone-400 hover:text-stone-600 underline">Change theme</button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-sans font-medium text-stone-600 mb-1.5 block">Name on cover</label>
              <input data-testid="cover-names" value={formData.coverNames || ''}
                onChange={e => onChange('coverNames', e.target.value)}
                placeholder={`${formData.person1Name || 'Person 1'} & ${formData.person2Name || 'Person 2'}`}
                className="w-full text-sm font-sans px-3 py-2.5 border border-stone-200 rounded-xl bg-stone-50 focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label className="text-xs font-sans font-medium text-stone-600 mb-1.5 block">Date on cover</label>
              <input data-testid="cover-date" type="date" value={formData.coverDate || ''} onChange={e => onChange('coverDate', e.target.value)}
                className="w-full text-sm font-sans px-3 py-2.5 border border-stone-200 rounded-xl bg-stone-50 focus:border-primary focus:outline-none" />
            </div>
          </div>

          <div>
            <label className="text-xs font-sans font-medium text-stone-600 mb-1.5 block">Venue on cover (optional)</label>
            <input data-testid="cover-venue" value={formData.coverVenue || ''} onChange={e => onChange('coverVenue', e.target.value)}
              placeholder="e.g. Operahuset, Oslo"
              className="w-full text-sm font-sans px-3 py-2.5 border border-stone-200 rounded-xl bg-stone-50 focus:border-primary focus:outline-none" />
          </div>

          <div>
            <label className="text-xs font-sans font-medium text-stone-600 mb-2 block">Upload your own cover image (optional)</label>
            <div className="border-2 border-dashed border-stone-200 rounded-2xl p-6 text-center hover:border-primary/40 transition-colors cursor-pointer">
              <Upload className="w-6 h-6 text-stone-300 mx-auto mb-2" />
              <p className="text-xs font-sans text-stone-400">Drag and drop, or click to choose</p>
              <p className="text-[10px] font-sans text-stone-300 mt-0.5">JPG, PNG – maks 10MB</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
